import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PersonDetails, PersonTitle } from "../types/interfaces";
import { API_BASE_URL } from "../config";
import PageHeader from "../components/PageHeader";
import { TYPE_LABELS } from "../utils/titleType";
import { formatLifespan } from "../utils/personMeta";

/**
 * Everything a person has been in, each credit linking on to where it streams.
 *
 * The credits are a list rather than the poster grid the search results use:
 * they come from Watchmode's title list, which carries no images, and two dozen
 * empty poster frames look like a page that failed to load.
 */
export default function PersonPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [person, setPerson] = useState<PersonDetails | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!id) return;

        let cancelled = false;
        const fetchPerson = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/person/${id}`);
                if (!response.ok) throw new Error(`Person request failed: ${response.status}`);
                const data: PersonDetails = await response.json();
                if (!cancelled) setPerson(data);
            } catch (error) {
                console.error("Error fetching person:", error);
                if (!cancelled) setFailed(true);
            }
        };

        fetchPerson();
        return () => {
            cancelled = true;
        };
    }, [id]);

    if (failed) {
        return (
            <div className="details-container">
                <PageHeader />
                <p className="empty-state">
                    Could not load this person. <a href="/">Start a new search</a>.
                </p>
            </div>
        );
    }

    if (!person) {
        return (
            <div className="details-container">
                <PageHeader />
                <div className="details-loading" aria-busy="true" aria-label="Loading person">
                    <div className="skeleton skeleton-poster" />
                    <div className="skeleton skeleton-line" style={{ width: "40%" }} />
                    <div className="skeleton skeleton-line" style={{ width: "90%" }} />
                    <div className="skeleton skeleton-line" style={{ width: "75%" }} />
                </div>
            </div>
        );
    }

    const lifespan = formatLifespan(person.date_of_birth, person.date_of_death);
    const shown = person.titles?.length || 0;

    return (
        <div className="details-container">
            <PageHeader />

            <div className="summary-info">
                {person.headshot_url && (
                    <div className="person-headshot">
                        <img src={person.headshot_url} alt="" />
                    </div>
                )}
                <div className="summary-text">
                    <h1>{person.name}</h1>
                    <div className="summary-meta">
                        {person.profession ? (
                            <span className="badge">{person.profession}</span>
                        ) : null}
                        {lifespan ? <span>{lifespan}</span> : null}
                        {person.place_of_birth ? <span>{person.place_of_birth}</span> : null}
                    </div>
                    {person.biography ? (
                        <p className="summary-plot">{person.biography}</p>
                    ) : null}
                </div>
            </div>

            <div className="person-credits">
                <h2 className="section-heading">Titles</h2>
                {shown > 0 ? (
                    <>
                        <p className="results-count">
                            {/* Prolific actors have hundreds of credits and the
                                back-end sends the most popular two dozen. */}
                            {person.total_titles > shown
                                ? `Showing ${shown} of ${person.total_titles}, most popular first`
                                : `${shown} ${shown === 1 ? "title" : "titles"}`}
                        </p>
                        <ul className="credit-list">
                            {person.titles.map((title: PersonTitle) => (
                                <li key={title.id}>
                                    <button
                                        className="credit-row"
                                        onClick={() => navigate(`/movieShowDetailsPage/${title.id}`)}
                                    >
                                        <span className="credit-title">{title.title}</span>
                                        <span className="credit-meta">
                                            {title.year ? <span>{title.year}</span> : null}
                                            {title.type ? (
                                                <span className="badge">
                                                    {TYPE_LABELS[title.type] || title.type}
                                                </span>
                                            ) : null}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </>
                ) : (
                    <p className="empty-state">No titles found for this person.</p>
                )}
            </div>
        </div>
    );
}
